import {
  DEFAULT_LANGUAGE,
  LOCAL_STORAGE_KEY,
  PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY,
  PREFERRED_LANGUAGE_USER_SELECTED_KEY,
  PREFERRED_LANGUAGE_INVITE_ACK_KEY,
  SUPPORTED_LANGUAGES,
} from "utilities/Constant";
import type { MyLoanInfo } from "pages/application/applicationService";

export function selectActiveLoan(
  loans: MyLoanInfo[] | undefined,
  nhDealApplicationId?: string | null,
): MyLoanInfo | undefined {
  if (!loans?.length || !nhDealApplicationId?.trim()) {
    return undefined;
  }

  return loans.find((loan) => loan.nhDealApplicationId === nhDealApplicationId);
}

export function selectLoanForPreferredLanguage(
  loans: MyLoanInfo[] | undefined,
  nhDealApplicationId?: string | null,
): MyLoanInfo | undefined {
  if (!loans?.length) {
    return undefined;
  }

  const contextLoan = selectActiveLoan(loans, nhDealApplicationId);
  if (contextLoan) {
    return contextLoan;
  }

  const loanRequiringAck = loans.find((loan) => loan.showAcknowledgementModal);
  if (loanRequiringAck) {
    return loanRequiringAck;
  }

  return loans.length === 1 ? loans[0] : undefined;
}

export type ResolveDisplayedLanguageOptions = {
  isExplicitSelection: boolean;
  isMyLoansPage: boolean;
  hasApplicationContext: boolean;
  isPostLosActiveLoan: boolean;
  userDbLanguage?: string | null;
  activeLoan?: Pick<MyLoanInfo, "dealPreferredLanguage" | "loanNumber" | "showAcknowledgementModal"> | null;
  reduxLanguage?: string | null;
  hasAcknowledgedSpanishPrompt?: boolean;
};

export type ShouldDeferDealTranslationUntilAckOptions = {
  showAcknowledgementModal?: boolean;
  isPostLos?: boolean;
};

export function shouldDeferDealTranslationUntilAck({
  showAcknowledgementModal,
  isPostLos,
}: ShouldDeferDealTranslationUntilAckOptions): boolean {
  return Boolean(showAcknowledgementModal) && !isPostLos;
}

export function resolveDisplayedPreferredLanguage(options: ResolveDisplayedLanguageOptions): string {
  const {
    isExplicitSelection,
    isMyLoansPage,
    hasApplicationContext,
    isPostLosActiveLoan,
    userDbLanguage,
    activeLoan,
    reduxLanguage,
    hasAcknowledgedSpanishPrompt,
  } = options;

  const inApplication = !isMyLoansPage && hasApplicationContext && Boolean(activeLoan);
  const shouldDeferToUserDbUntilAck =
    Boolean(activeLoan) &&
    !hasAcknowledgedSpanishPrompt &&
    shouldDeferDealTranslationUntilAck({
      showAcknowledgementModal: activeLoan?.showAcknowledgementModal,
      isPostLos: isPostLosActiveLoan,
    });

  // Post-LOS application: always the deal language, even after a user toggle elsewhere.
  if (inApplication && isPostLosActiveLoan) {
    return normalizeToAppLanguage(activeLoan?.dealPreferredLanguage);
  }

  if (isExplicitSelection) {
    return getPreferredLanguage(reduxLanguage);
  }

  if (shouldDeferToUserDbUntilAck) {
    return userDbLanguage?.trim()
      ? normalizeToAppLanguage(userDbLanguage)
      : getPreferredLanguage(reduxLanguage);
  }

  if (inApplication) {
    return normalizeToAppLanguage(activeLoan?.dealPreferredLanguage);
  }

  if (isMyLoansPage) {
    if (userDbLanguage?.trim()) {
      return normalizeToAppLanguage(userDbLanguage);
    }

    return getPreferredLanguage(reduxLanguage);
  }

  return getPreferredLanguage(reduxLanguage);
}

export function resolveLanguageForLoanEntry(
  loan: Pick<MyLoanInfo, "dealPreferredLanguage" | "loanNumber" | "preferredLanguage" | "showAcknowledgementModal">,
  reduxLanguage?: string | null,
  hasAcknowledgedSpanishPrompt?: boolean,
): string {
  return resolveDisplayedPreferredLanguage({
    isExplicitSelection: hasExplicitUserLanguageSelection(),
    isMyLoansPage: false,
    hasApplicationContext: true,
    isPostLosActiveLoan: Boolean(loan.loanNumber && loan.loanNumber !== "TBD"),
    userDbLanguage: loan.preferredLanguage,
    hasAcknowledgedSpanishPrompt,
    activeLoan: loan,
    reduxLanguage,
  });
}

export function resolveLanguageForMyLoansLanding(
  userDbLanguage?: string | null,
  reduxLanguage?: string | null,
): string {
  return resolveDisplayedPreferredLanguage({
    isExplicitSelection: hasExplicitUserLanguageSelection(),
    isMyLoansPage: true,
    hasApplicationContext: false,
    isPostLosActiveLoan: false,
    userDbLanguage,
    reduxLanguage,
  });
}

export function shouldPersistLoanEntryUserLanguage(
  resolvedLanguage: string,
  reduxLanguage?: string | null,
): boolean {
  if (!hasExplicitUserLanguageSelection()) {
    return true;
  }

  return normalizeToAppLanguage(resolvedLanguage) === getPreferredLanguage(reduxLanguage);
}

export function getPersistedPreferredLanguage(): string | null {
  const saved = globalThis.sessionStorage.getItem(LOCAL_STORAGE_KEY);
  return saved?.trim() ? normalizeToAppLanguage(saved) : null;
}

export function setPersistedPreferredLanguage(value: string): void {
  globalThis.sessionStorage.setItem(LOCAL_STORAGE_KEY, normalizeToAppLanguage(value));
}

export function normalizeToAppLanguage(value?: string | null): string {
  if (!value?.trim()) {
    return DEFAULT_LANGUAGE;
  }

  switch (value.trim().toLowerCase()) {
    case "english":
      return "English";
    case "spanish":
    case "español":
    case "espanol":
      return "Español";
    default:
      return SUPPORTED_LANGUAGES.includes(value) ? value : DEFAULT_LANGUAGE;
  }
}

export function toUserDbPreferredLanguage(appLanguage: string): string {
  return normalizeToAppLanguage(appLanguage) === "Español" ? "Spanish" : "English";
}

export function getPreferredLanguage(reduxLanguage?: string | null): string {
  return normalizeToAppLanguage(
    getPersistedPreferredLanguage() ?? reduxLanguage ?? DEFAULT_LANGUAGE,
  );
}

export function isSpanishLanguage(value?: string | null): boolean {
  return normalizeToAppLanguage(value) === "Español";
}

export function hasExplicitUserLanguageSelection(): boolean {
  return globalThis.sessionStorage.getItem(PREFERRED_LANGUAGE_USER_SELECTED_KEY) === "true";
}

export function markExplicitUserLanguageSelection(): void {
  globalThis.sessionStorage.setItem(PREFERRED_LANGUAGE_USER_SELECTED_KEY, "true");
}

export function getExplicitUserLanguage(): string | null {
  if (!hasExplicitUserLanguageSelection()) {
    return null;
  }

  return getPersistedPreferredLanguage();
}

export function hasPreferredLanguageDealSyncCompleted(): boolean {
  return globalThis.sessionStorage.getItem(PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY) === "true";
}

export function markPreferredLanguageDealSyncCompleted(): void {
  globalThis.sessionStorage.setItem(PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY, "true");
}

export function clearPreferredLanguageStorageOnLogout(): void {
  globalThis.sessionStorage.removeItem(LOCAL_STORAGE_KEY);
  globalThis.sessionStorage.removeItem(PREFERRED_LANGUAGE_USER_SELECTED_KEY);
  globalThis.sessionStorage.removeItem(PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY);
  clearInviteAckKeys();
  globalThis.localStorage.removeItem(LOCAL_STORAGE_KEY);
}

export function getInviteAckStorageKey(inviteKey?: string | null): string {
  const trimmed = inviteKey?.trim();
  return trimmed ? `${PREFERRED_LANGUAGE_INVITE_ACK_KEY}:${trimmed}` : PREFERRED_LANGUAGE_INVITE_ACK_KEY;
}

function clearInviteAckKeys(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < globalThis.sessionStorage.length; i += 1) {
    const key = globalThis.sessionStorage.key(i);
    if (!key) {
      continue;
    }
    if (
      key === PREFERRED_LANGUAGE_INVITE_ACK_KEY ||
      key.startsWith(`${PREFERRED_LANGUAGE_INVITE_ACK_KEY}:`)
    ) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => globalThis.sessionStorage.removeItem(key));
}

// ADO-535683: marks that the borrower acknowledged (or cancelled) the Spanish invite modal
// so the modal is not re-shown after the page reload triggered by acknowledge.
export function markInviteAckDone(inviteKey?: string | null): void {
  globalThis.sessionStorage.setItem(getInviteAckStorageKey(inviteKey), "true");
}

export function hasInviteAckDone(inviteKey?: string | null): boolean {
  if (inviteKey?.trim()) {
    return globalThis.sessionStorage.getItem(getInviteAckStorageKey(inviteKey)) === "true";
  }

  for (let i = 0; i < globalThis.sessionStorage.length; i += 1) {
    const key = globalThis.sessionStorage.key(i);
    if (!key) {
      continue;
    }
    if (
      key === PREFERRED_LANGUAGE_INVITE_ACK_KEY ||
      key.startsWith(`${PREFERRED_LANGUAGE_INVITE_ACK_KEY}:`)
    ) {
      if (globalThis.sessionStorage.getItem(key) === "true") {
        return true;
      }
    }
  }

  return false;
}

export function buildSyncPreferredLanguageRequest(
  userId: string,
  appLanguage: string,
): { userId: string; preferredLanguage: string } {
  return {
    userId,
    preferredLanguage: toUserDbPreferredLanguage(appLanguage),
  };
}
